/**
 * useStageMoveSummary — the one-line description of a single stage move.
 *
 * Used by the collapsed chat row (`InlineProposalCard`) to replace the
 * generic action-type icons with the actual delta when a proposal is exactly
 * one stage-association add. Returns null for anything else so the row keeps
 * its icon summary.
 *
 * Describes only what the PROPOSAL says — the record it moves and the stage it
 * moves to. It deliberately does not report a "current" stage: a collapsed row
 * has no room for a diff, and the two ways to get one here are both wrong. The
 * payload's `original_target_entity_id` is a copy taken when the proposal was
 * written and drifts once the record moves; the projected record already has
 * the proposal merged in. The real current → proposed diff lives on the
 * expanded card, read live. See FRONTEND_PATTERNS.md § Live vs. projected.
 */

import { useMemo } from 'react';

import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import type { ProposedAction } from '@/lib/proposals/core/types';
import { isAssociationAddedPayload } from '@/lib/proposals/core/types';
import { getActionKey } from '@/lib/proposals/registry/utils';

/** The pipeline that contains the given stage id. */
function findPipelineForStage(
  pipelines: readonly Pipeline[],
  stageId: string | null | undefined
): Pipeline | undefined {
  if (!stageId) {
    return undefined;
  }
  return pipelines.find(p => p.stages.some(s => s.entity_id === stageId));
}

export interface StageMoveSummary {
  entityType: string;
  /** Display name of the record being moved, when the backend enriched it. */
  objectLabel: string | undefined;
  proposedId: string;
  /** Label of the proposed stage once the schema resolves it. */
  proposedLabel: string | undefined;
}

function singleStageMove(actions: readonly ProposedAction[] | undefined) {
  if (!actions || actions.length !== 1) {
    return null;
  }
  const [action] = actions;
  if (getActionKey(action) !== 'timeline_event:stage_association_added') {
    return null;
  }
  if (!isAssociationAddedPayload(action.payload)) {
    return null;
  }
  return {
    payload: action.payload,
    objectLabel: action.enriched_data?.object_label || undefined,
  };
}

export function useStageMoveSummary(
  actions: readonly ProposedAction[] | undefined,
  orgId: number | undefined
): StageMoveSummary | null {
  const move = useMemo(() => singleStageMove(actions), [actions]);
  const payload = move?.payload;
  const objectLabel = move?.objectLabel;
  const entityType = payload?.entity_type_name;
  const { pipelines } = usePipelines(payload ? orgId : undefined, entityType);

  return useMemo(() => {
    if (!payload || !entityType) {
      return null;
    }
    const proposedId = payload.payload.target_entity_id;
    const pipeline = findPipelineForStage(pipelines, proposedId);
    const proposedLabel = pipeline?.stages?.find(
      s => s.entity_id === proposedId
    )?.label;
    return { entityType, objectLabel, proposedId, proposedLabel };
  }, [payload, objectLabel, entityType, pipelines]);
}
